import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Checkbox,
  InputAdornment,
  TableSortLabel
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw, Printer as PrintIcon, Search, FileText as TipoRapportoLogoIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';

export default function TipiRapporto() {
  const [tipi, setTipi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentTipo, setCurrentTipo] = useState(null);
  const [selected, setSelected] = useState([]);
  const { addJob } = useJob();
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    sort_order: 0
  });
  const [error, setError] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sort_order');
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    fetchTipi();
  }, []);

  const fetchTipi = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tipi-rapporto');
      setTipi(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching tipi rapporto:', err);
      setError('Failed to load data');
      toast.error('Errore nel caricamento dei tipi rapporto');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tipo = null) => {
    if (tipo) {
      setCurrentTipo(tipo);
      setFormData({
        code: tipo.code,
        label: tipo.label,
        sort_order: tipo.sort_order
      });
    } else {
      setCurrentTipo(null);
      setFormData({
        code: '',
        label: '',
        sort_order: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTipo(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentTipo) {
        await api.put(`/tipi-rapporto/${currentTipo.id}`, formData);
        toast.success('Tipo rapporto aggiornato con successo');
      } else {
        await api.post('/tipi-rapporto', formData);
        toast.success('Tipo rapporto creato con successo');
      }
      
      fetchTipi();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving tipo rapporto:', err);
      const message = err.response?.data?.message || 'Errore nel salvataggio';
      setError(message);
      toast.error(message);
    }
  };

  const openDeleteDialog = (tipo) => {
    setDeleteTarget(tipo);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/tipi-rapporto/${deleteTarget.id}`);
      toast.success('Tipo rapporto eliminato');
      closeDeleteDialog();
      fetchTipi();
    } catch (err) {
      console.error('Error deleting tipo rapporto:', err);
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelecteds = tipi.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  const handleBulkPrint = async () => {
    if (selected.length === 0) {
      toast.warning('Seleziona almeno un tipo rapporto');
      return;
    }

    try {
      const response = await api.post('/tipi-rapporto/report', {
        ids: selected
      });

      const { jobId } = response.data;
      addJob(jobId, 'tipo_rapporto');
      toast.info('Generazione report avviata. Controlla lo stato nella barra delle notifiche.');
      setSelected([]);
    } catch (err) {
      console.error('Error starting report generation:', err);
      toast.error('Errore nell\'avvio della generazione del report');
    }
  };

  const filteredTipi = tipi
    .filter((row) => {
      if (!filterText) return true;
      const search = filterText.toLowerCase();
      const code = row.code ? row.code.toString().toLowerCase() : '';
      const label = row.label ? row.label.toString().toLowerCase() : '';
      return code.includes(search) || label.includes(search);
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const key = orderBy || 'sort_order';
      const valA = a[key];
      const valB = b[key];
      if (key === 'sort_order' || key === 'id') {
        const numA = valA == null ? 0 : Number(valA);
        const numB = valB == null ? 0 : Number(valB);
        return isAsc ? numA - numB : numB - numA;
      }
      const strA = valA ? valA.toString().toLowerCase() : '';
      const strB = valB ? valB.toString().toLowerCase() : '';
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return 0;
    });

  return (
    <Box className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <TipoRapportoLogoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Gestione Tipi Rapporto
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Tipologie di rapporti (conto, carta, prestito, mutuo, ecc.) utilizzate nelle pratiche
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleBulkPrint}
              className="flex items-center gap-2"
            >
              <PrintIcon className="h-4 w-4" />
              Stampa PDF ({selected.length})
            </Button>
          )}
          <Button variant="outline" onClick={fetchTipi} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Tipo Rapporto
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="shadow-md">
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterText}
            onChange={handleFilterChange}
            placeholder="Cerca per codice o etichetta..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < tipi.length}
                    checked={tipi.length > 0 && selected.length === tipi.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'code'}
                    direction={orderBy === 'code' ? order : 'asc'}
                    onClick={() => handleRequestSort('code')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'label'}
                    direction={orderBy === 'label' ? order : 'asc'}
                    onClick={() => handleRequestSort('label')}
                  >
                    Etichetta
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'sort_order'}
                    direction={orderBy === 'sort_order' ? order : 'asc'}
                    onClick={() => handleRequestSort('sort_order')}
                  >
                    Ordinamento
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : tipi.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="py-8">
                    <Typography color="textSecondary">Nessun tipo rapporto trovato</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTipi
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((tipo) => {
                    const isSelected = selected.indexOf(tipo.id) !== -1;
                    return (
                    <TableRow 
                      key={tipo.id} 
                      hover
                      role="checkbox"
                      aria-checked={isSelected}
                      selected={isSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(event) => handleSelectOne(event, tipo.id)}
                        />
                      </TableCell>
                      <TableCell>{tipo.code}</TableCell>
                      <TableCell>{tipo.label}</TableCell>
                      <TableCell>{tipo.sort_order}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(tipo);
                          }}
                          size="small"
                        >
                          <EditIcon className="h-4 w-4" />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteDialog(tipo);
                          }}
                          size="small"
                        >
                          <DeleteIcon className="h-4 w-4" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )})
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTipi.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentTipo ? 'Modifica Tipo Rapporto' : 'Nuovo Tipo Rapporto'}
        </DialogTitle>
        <DialogContent dividers>
          <Box className="flex flex-col gap-4 pt-2">
            <TextField
              name="code"
              label="Codice"
              value={formData.code}
              onChange={handleChange}
              fullWidth
              required
              helperText="Identificativo univoco (es. CARTA_CREDITO)"
            />
            <TextField
              name="label"
              label="Etichetta"
              value={formData.label}
              onChange={handleChange}
              fullWidth
              required
              helperText="Nome visualizzato (es. Carta di Credito)"
            />
            <TextField
              name="sort_order"
              label="Ordinamento"
              type="number"
              value={formData.sort_order}
              onChange={handleChange}
              fullWidth
              helperText="Ordine di visualizzazione (0 = primo)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Questa azione è irreversibile.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Stai per eliminare il tipo rapporto:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
            {deleteTarget?.label || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Codice: <strong>{deleteTarget?.code || '-'}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
